import { type FormEvent, type ReactNode, useState } from 'react';

import { PasswordChecklist, PasswordInput } from '@/components/PasswordField';
import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import { Label } from '@/design-system/ui/Label';
import { passwordError } from '@escala/domain/password';

import { authErrorMessage } from '../authError';

/**
 * Senha nova e confirmação, com o medidor de força e os requisitos. Só envia quando a
 * senha cumpre as regras e as duas coincidem; o erro de `onSubmit` aparece no formulário.
 */
export default function NewPasswordForm({
  action,
  onSubmit,
  before,
  after,
  guard,
}: {
  action: ReactNode;
  onSubmit: (password: string) => Promise<void>;
  /** Campos antes da senha (o e-mail do convite, por exemplo). */
  before?: ReactNode;
  /** Depois da confirmação, antes do botão (o aceite dos Termos no convite). */
  after?: ReactNode;
  /** Conferência extra no envio (o aceite marcado); `false` não envia. */
  guard?: () => boolean;
}) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [shown, setShown] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mismatch = confirmation.length > 0 && confirmation !== password;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);

    const invalid = passwordError(password);

    const allowed = guard ? guard() : true;

    if (invalid) return setError(invalid);
    if (password !== confirmation) return setError(null);
    if (!allowed) return setError(null);

    setBusy(true);
    setError(null);

    try {
      await onSubmit(password);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form noValidate className="flex flex-col gap-4.5" onSubmit={submit}>
      {before}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="new-password">Senha</Label>
        <PasswordInput
          id="new-password"
          autoComplete="new-password"
          placeholder="Crie uma senha"
          className="h-11"
          shown={shown}
          onToggle={() => setShown((s) => !s)}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <PasswordChecklist password={password} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="new-password-confirmation">Confirmar senha</Label>
        <PasswordInput
          id="new-password-confirmation"
          autoComplete="new-password"
          placeholder="Repita a senha"
          className="h-11"
          shown={shown}
          invalid={mismatch || (tried && confirmation !== password)}
          aria-describedby={mismatch ? 'new-password-mismatch' : undefined}
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
        />
        {mismatch || (tried && confirmation !== password) ? (
          <span id="new-password-mismatch" role="alert" className="text-caption text-status-conflict">
            As senhas não coincidem.
          </span>
        ) : null}
      </div>
      {after}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <Button type="submit" size="lg" className="h-12" disabled={busy}>
        {action}
      </Button>
    </form>
  );
}
